<script lang="ts">
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { AlertTriangle, UploadCloud } from 'lucide-svelte';
  import { catalogApi } from '$lib/api/catalog';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { ImportResult } from '$lib/types';

  const { onClose }: { onClose: () => void } = $props();

  const queryClient = useQueryClient();
  let file = $state<File | null>(null);
  let preview = $state<ImportResult | null>(null);
  let error = $state('');
  // Off by default — a conflict means an admin hand-priced this product
  // since the last import, so silently overwriting it needs an explicit
  // opt-in (see catalogImportCommit's doc comment).
  let forceOverwriteConflicts = $state(false);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['catalog-products'] });
    queryClient.invalidateQueries({ queryKey: ['catalog-categories'] });
    queryClient.invalidateQueries({ queryKey: ['catalog-health'] });
  }

  const previewMutation = createMutation(() => ({
    mutationFn: (f: File) => catalogApi.importPreview(f),
    onSuccess: (data) => {
      preview = data;
      error = '';
    },
    onError: (err: unknown) => (error = getErrorMessage(err))
  }));

  const commitMutation = createMutation(() => ({
    mutationFn: (f: File) => catalogApi.importCommit(f, forceOverwriteConflicts),
    onSuccess: (data) => {
      invalidate();
      const skipped = data.summary.skipped_conflicts ?? 0;
      toast.success(
        `Import complete — ${data.summary.create} created, ${data.summary.update} updated` +
          (skipped > 0 ? `, ${skipped} conflict${skipped === 1 ? '' : 's'} skipped.` : '.')
      );
      onClose();
    },
    onError: (err: unknown) => (error = getErrorMessage(err))
  }));

  function onFileChange(e: Event) {
    const chosen = (e.currentTarget as HTMLInputElement).files?.[0] ?? null;
    file = chosen;
    preview = null;
    error = '';
    if (chosen) previewMutation.mutate(chosen);
  }

  function confirm() {
    if (file) commitMutation.mutate(file);
  }

  const hasErrors = $derived((preview?.summary.error ?? 0) > 0);
  const hasConflicts = $derived((preview?.summary.conflict ?? 0) > 0);
  const pending = $derived(previewMutation.isPending || commitMutation.isPending);
</script>

<Modal title="Import products from CSV" onClose={pending ? () => {} : onClose} wide>
  <div class="space-y-4">
    <p class="tym-small">
      Columns understood: part_number, name (required), description, category, unit_of_measure, list_price,
      base_cost_price, ownership, functional_category, functional_type. Matching is by part number
      (case-insensitive) — a match updates that product, no match creates a new one. Columns your CSV omits
      are left untouched on existing products.
    </p>

    <div>
      <label class="label" for="import-file">CSV file</label>
      <input
        id="import-file"
        type="file"
        accept=".csv,text/csv"
        class="input"
        onchange={onFileChange}
        disabled={pending}
      />
    </div>

    {#if previewMutation.isPending}
      <p class="tym-loading-state">Scanning file&hellip;</p>
    {:else if error}
      <p class="tym-error-state">{error}</p>
    {:else if preview}
      <div class="flex flex-wrap gap-2">
        <span class="tym-badge tym-badge-success">{preview.summary.create} create</span>
        <span class="tym-badge tym-badge-info">{preview.summary.update} update</span>
        <span class="tym-badge tym-badge-neutral">{preview.summary.unchanged} unchanged</span>
        {#if hasConflicts}
          <span class="tym-badge tym-badge-warning">{preview.summary.conflict} conflict</span>
        {/if}
        {#if hasErrors}
          <span class="tym-badge tym-badge-danger">{preview.summary.error} error</span>
        {/if}
      </div>

      {#if hasConflicts}
        <label class="flex items-start gap-2 text-[12.5px]" style="color: var(--tym-warning)">
          <input type="checkbox" class="mt-0.5" bind:checked={forceOverwriteConflicts} />
          <span>
            <strong>{preview.summary.conflict}</strong> row{preview.summary.conflict === 1 ? '' : 's'} would change a
            price an admin manually edited since the last import. By default these are skipped — check this to
            force-overwrite them anyway.
          </span>
        </label>
      {/if}

      {#if hasErrors}
        <p class="flex items-center gap-1.5 text-[12.5px] font-semibold text-[#C8412B]">
          <AlertTriangle class="h-3.5 w-3.5" /> Fix the rows below and re-upload — nothing will be imported while
          any row has an error.
        </p>
      {/if}

      <div class="tym-table-wrap tym-table-scroll max-h-[320px]">
        <table class="tym-table">
          <thead>
            <tr>
              <th class="w-[50px]">Row</th>
              <th>Part number</th>
              <th>Name</th>
              <th class="w-[110px]">Change</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {#each preview.results as row (row.row)}
              <tr class={row.change === 'conflict' ? 'bg-[#FFF9EC]' : ''}>
                <td>{row.row}</td>
                <td class="font-mono text-[12px]">{row.part_number}</td>
                <td>{row.name}</td>
                <td>
                  <span
                    class="tym-badge {row.change === 'error'
                      ? 'tym-badge-danger'
                      : row.change === 'create'
                        ? 'tym-badge-success'
                        : row.change === 'update'
                          ? 'tym-badge-info'
                          : row.change === 'conflict'
                            ? 'tym-badge-warning'
                            : 'tym-badge-neutral'}"
                  >
                    {row.change}
                  </span>
                </td>
                <td class="text-[12px] text-dark-gray">
                  {#if row.change === 'error'}
                    {row.error}
                  {:else if (row.change === 'update' || row.change === 'conflict') && row.current}
                    changes: {Object.keys(row.current).join(', ')}
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" class="btn-secondary" onclick={onClose} disabled={pending}>Cancel</button>
      <button
        type="button"
        class="btn-primary"
        disabled={!preview || hasErrors || pending}
        onclick={confirm}
      >
        <UploadCloud class="h-4 w-4" />
        {commitMutation.isPending ? 'Importing…' : 'Confirm import'}
      </button>
    </div>
  </div>
</Modal>
